import ReactDOM from "react-dom";
import React, { useState } from "react";

const ROLES = ["GE", "CE", "E"];
const ROLE_MULTI_COLLEGE = "GE"; // seul ce rôle peut être rattaché à plusieurs collèges

// Mot de passe "jeton" : chaîne aléatoire communiquée à l'utilisateur pour sa
// première connexion, pas un mot de passe mémorisable.
function generateToken() {
  const bytes = new Uint8Array(9);
  window.crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, "").slice(0, 12);
}

/**
 * AddUserForm — popin de création d'un utilisateur "Dispositif", même
 * format que UserInfoModal/ConfirmModal (header titre + croix, body,
 * footer avec deux boutons).
 *
 * Usage :
 *   <AddUserForm colleges={colleges} onCancel={() => setShowAddForm(false)} onCreate={handleCreate} />
 */
export function AddUserForm({ colleges, onCancel, onCreate }) {
  const [form, setForm] = useState({
    utcNom: "",
    utcMail: "",
    utcPwd: generateToken(),
    comIds: [],
    utcRole: "",
    utcActif: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isMultiCollege = form.utcRole === ROLE_MULTI_COLLEGE;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      if (name === "utcActif") return { ...f, utcActif: value === "true" };
      if (name === "utcRole") {
        // CE/E ne peuvent garder qu'un seul collège : on tronque au besoin.
        const comIds = value !== ROLE_MULTI_COLLEGE ? f.comIds.slice(0, 1) : f.comIds;
        return { ...f, utcRole: value, comIds };
      }
      return { ...f, [name]: value };
    });
  };

  const toggleCollege = (comId) => {
    const id = String(comId);
    setForm((f) => ({
      ...f,
      comIds: f.comIds.includes(id) ? f.comIds.filter((v) => v !== id) : [...f.comIds, id],
    }));
  };

  const selectSingleCollege = (value) => {
    setForm((f) => ({ ...f, comIds: value ? [value] : [] }));
  };

  const regenerateToken = () => {
    setForm((f) => ({ ...f, utcPwd: generateToken() }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        utcNom: form.utcNom.trim(),
        utcMail: form.utcMail.trim(),
        utcPwd: form.utcPwd,
        comIds: form.comIds.map(Number),
        utcRole: form.utcRole,
        utcActif: form.utcActif,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <>
      <div
        className="modal modal-blur fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ zIndex: 2000 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>

              {/* HEADER */}
              <div className="modal-header">
                <h5 className="modal-title">Ajouter un utilisateur</h5>
                <button type="button" className="btn-close" onClick={onCancel} disabled={saving} />
              </div>

              {/* BODY */}
              <div className="modal-body">
                <div className="row pb-2">
                  <div className="col-6">
                    <label className="form-label"><strong>Nom *</strong></label>
                    <input
                      type="text"
                      name="utcNom"
                      className="form-control"
                      value={form.utcNom}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label"><strong>Email *</strong></label>
                    <input
                      type="email"
                      name="utcMail"
                      className="form-control"
                      value={form.utcMail}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="row pb-2">
                  <div className="col-12">
                    <label className="form-label">
                      <strong>Mot de passe *</strong>
                      <span className="text-secondary ms-1" style={{ fontWeight: "normal" }}>
                        (jeton à communiquer à l'utilisateur, pour sa première connexion uniquement)
                      </span>
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        name="utcPwd"
                        className="form-control"
                        style={{ fontFamily: "monospace" }}
                        value={form.utcPwd}
                        onChange={handleChange}
                        required
                      />
                      <button type="button" className="btn btn-secondary" onClick={regenerateToken}>
                        Régénérer
                      </button>
                    </div>
                  </div>
                </div>
                <div className="row pb-2">
                  <div className="col-6">
                    <label className="form-label"><strong>Rôle</strong></label>
                    <select name="utcRole" value={form.utcRole} onChange={handleChange} className="form-control">
                      <option value="">—</option>
                      {ROLES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label"><strong>État</strong></label>
                    <select name="utcActif" value={String(form.utcActif)} onChange={handleChange} className="form-control">
                      <option value="true">Actif</option>
                      <option value="false">Inactif</option>
                    </select>
                  </div>
                </div>
                <div className="row pb-2">
                  <div className="col-12">
                    <label className="form-label">
                      <strong>{isMultiCollege ? "Collège(s)" : "Collège"}</strong>
                      {!isMultiCollege && (
                        <span className="text-secondary ms-1" style={{ fontWeight: "normal" }}>
                          (plusieurs collèges réservé au rôle GE)
                        </span>
                      )}
                    </label>
                    {isMultiCollege ? (
                      <div className="form-control" style={{ height: "auto", maxHeight: 160, overflowY: "auto" }}>
                        {colleges.map((c) => (
                          <div className="form-check" key={c.comId}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`add-college-${c.comId}`}
                              checked={form.comIds.includes(String(c.comId))}
                              onChange={() => toggleCollege(c.comId)}
                            />
                            <label className="form-check-label" htmlFor={`add-college-${c.comId}`}>
                              {c.comLib}
                            </label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <select
                        className="form-control"
                        value={form.comIds[0] || ""}
                        onChange={(e) => selectSingleCollege(e.target.value)}
                      >
                        <option value="">—</option>
                        {colleges.map((c) => (
                          <option key={c.comId} value={c.comId}>{c.comLib}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                {error && <div className="text-danger">{error}</div>}
              </div>

              {/* FOOTER */}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="btn fr-btn" disabled={saving}>
                  {saving ? "Création…" : "Créer"}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>

      {/* BACKDROP */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1999 }}
        onClick={saving ? undefined : onCancel}
      />
    </>,
    document.body
  );
}
